import { Link, useNavigate } from "react-router-dom";
import { Home, Heart, Video, User, LogOut } from "lucide-react";

import { signOut } from "@/lib/auth";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";

type SidebarItemKey = "events" | "feed" | "matches" | "pitch" | "profile";

interface Props {
  active: SidebarItemKey;
  /** When set, exposes a per-event "Feed" link in the navigation. */
  feedSlug?: string;
}

export function DashboardSidebar({ active, feedSlug }: Props) {
  const navigate = useNavigate();

  const onSignOut = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  return (
    <>
      <aside className="hidden border-r border-[#e2e8f0] bg-white px-5 py-8 md:sticky md:top-16 md:flex md:h-[calc(100vh-64px)] md:flex-col md:gap-1 md:self-start md:overflow-y-auto">
        <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
          Navigation
        </p>
        <NavLink to="/me/meetups" label="My events" icon={Home} active={active === "events"} />
        {feedSlug ? (
          <NavLink
            to={`/e/${feedSlug}/feed`}
            label="Feed"
            icon={Heart}
            active={active === "feed"}
          />
        ) : null}
        <NavLink to="/me/matches" label="My matches" icon={Heart} active={active === "matches"} />
        <NavLink to="/me/pitch" label="My pitch" icon={Video} active={active === "pitch"} />

        <p className="mb-2 mt-5 px-3 text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
          Account
        </p>
        <NavLink to="/me/account" label="Profile" icon={User} active={active === "profile"} />
        <button
          type="button"
          onClick={onSignOut}
          className="mt-1 flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-[14px] font-medium text-[#475569] transition hover:bg-[#f8fafc] hover:text-[#1a1a1a]"
        >
          <LogOut className="h-4 w-4 opacity-70" />
          Sign out
        </button>
      </aside>
      {/* Mobile (<md) shows the same primary destinations in a bottom tab bar
          since the sidebar is hidden. Sign out is reachable through Profile. */}
      <MobileBottomNav
        items={[
          { to: "/me/meetups", label: "Events", icon: Home, end: false },
          { to: "/me/matches", label: "Matches", icon: Heart },
          { to: "/me/pitch", label: "Pitch", icon: Video },
          { to: "/me/account", label: "Profile", icon: User },
        ]}
        showSignOut={false}
      />
    </>
  );
}

function NavLink({
  to,
  label,
  icon: Icon,
  active,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-[14px] transition ${
        active
          ? "bg-[#eff6ff] font-semibold text-[#2563eb]"
          : "font-medium text-[#475569] hover:bg-[#f8fafc] hover:text-[#1a1a1a]"
      }`}
    >
      <Icon className={`h-4 w-4 ${active ? "" : "opacity-70"}`} />
      {label}
    </Link>
  );
}
